import {
	Button,
	Code,
	Container,
	Group,
	Stack,
	TextInput,
	Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
	IconAsteriskSimple,
	IconLogin2,
	IconUserCheck,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WHITE } from '../../const/constants';
import { useAuth } from '../../hooks/useAuth';
import { mutateLogin } from './api/useLogin';
import type { LoginDto } from './types/LoginDto';

const defaultLogin: LoginDto = {
	username: '',
	password: '',
};

export function Login() {
	const [login, setLogin] = useState<LoginDto>(defaultLogin);
	const loginMutation = mutateLogin(login);
	const auth = useAuth();
	const navigate = useNavigate();

	const loginForm = useForm<LoginDto>({
		mode: 'controlled',
		initialValues: defaultLogin,
		validate: {
			username: (value) => {
				if (!value) {
					return 'Username or email is required';
				}
				if (value.length > 250) {
					return 'Username or email cannot exceed 250 characters';
				}
				return null;
			},
			password: (value) => {
				if (!value) {
					return 'Password is required';
				}
				if (value.length > 250) {
					return 'Password cannot exceed 250 characters';
				}
				return null;
			},
		},
		onValuesChange: () => {
			setLogin(loginForm.getValues());
		},
	});

	useEffect(() => {
		document.body.style.backgroundColor = WHITE;
	}, []);

	useEffect(() => {
		if (loginMutation.data?.isValid && loginMutation.data.value) {
			auth.login(loginMutation.data.value);

			navigate(`/${loginMutation.data?.value.organisation}/`);
		}
	}, [loginMutation.data]);

	const codeProps = {
		style: {
			backgroundColor: 'transparent',
		},
	};

	return (
		<>
			<Container>
				<Stack justify={'start'} w={'400px'}>
					<Title fw={600} order={2}>
						Welcome Back
					</Title>
					<Code>Glad to see you again, please login below.</Code>
					<TextInput
						id={'username-field'}
						label={'Username or Email'}
						placeholder="Insert email or username..."
						w={'400px'}
						rightSection={<IconUserCheck size="1.25rem" />}
						key={loginForm.key('username')}
						{...loginForm.getInputProps('username')}
					/>
					<TextInput
						id={'password-field'}
						label={'Password'}
						placeholder="Insert password..."
						w={'400px'}
						type={'password'}
						rightSection={<IconAsteriskSimple size="1.25rem" />}
						key={loginForm.key('password')}
						{...loginForm.getInputProps('password')}
					/>
					<Code>
						If you have forgotten your password, contact MedEpos support.
					</Code>
					{loginMutation.data && !loginMutation.data.isValid ? (
						<>
							<Code
								style={{
									backgroundColor: 'transparent',
									color: 'red',
								}}
							>
								Username or password doesn't match, please try again.
							</Code>
						</>
					) : (
						<></>
					)}
					<Group justify={'end'}>
						<Stack>
							<Button
								color={'medepos.1'}
								w={'fit-content'}
								rightSection={<IconLogin2 />}
								type={'submit'}
								onClick={async () => {
									// loginMutation.mutate();
									await loginMutation.mutateAsync();
								}}
								data-cy={'login-button'}
								loading={loginMutation.isLoading}
							>
								Login
							</Button>
						</Stack>
					</Group>
				</Stack>
			</Container>
		</>
	);
}
